// code-guardian-ignore-duplicates: parallel master-page shell: the StoreMasterPage tab strip + action slot drawn inline on purpose so every tabbed page matches
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClipboardList, Route, Target } from 'lucide-react';
import TasksPage from './TasksPage';
import VisitTargetsTab, { DISCARD_TARGETS_PROMPT, type VisitQuestionnaire } from '../components/store-visits/VisitTargetsTab';
import PreJourneyPlanTab, { DISCARD_PLAN_PROMPT } from '../components/store-visits/PreJourneyPlanTab';
import { usePageLine } from '../hooks/usePageLine';
import { tasksAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { splitTasksByStatus } from '../utils/taskListFilter';

/**
 * Master Data → Store Visits Configuration (once "Store Visit Questionnaire"; the address
 * and the menu code are unchanged).
 *
 * Three tabs of one page:
 *  - "Visit targets" — which roles visit stores, on how many days a week and how many
 *    stores a day (components/store-visits/VisitTargetsTab).
 *  - "Questionnaires" — the questions answered at a store: the Tasks page, filtered to
 *    task_type='store_visit', in `embedded` mode.
 *  - "Pre-Journey Plan" — which roles get a monthly visit plan and its visit rules
 *    (components/store-visits/PreJourneyPlanTab).
 *
 * The questionnaires are deliberately not their own editor. An earlier version
 * reimplemented a cut-down question builder here and it drifted immediately: seven answer
 * types instead of ten, no option sources, no AI extraction, no per-question reorder.
 * Rendering the real page means parity is permanent rather than a copy that decays.
 *
 * The shell follows StoreMasterPage: the tab lives in `?tab=`, the strip and the open
 * tab's primary button share one row (each tab portals its own button into the slot), and
 * a tab is mounted only while it is open.
 */

type Tab = 'targets' | 'questionnaires' | 'plan';

const TABS: { key: Tab; label: string; icon: typeof Target }[] = [
  { key: 'targets', label: 'Visit targets', icon: Target },
  { key: 'questionnaires', label: 'Questionnaires', icon: ClipboardList },
  { key: 'plan', label: 'Pre-Journey Plan', icon: Route },
];

export default function StoreVisitFormsPage() {
  const pageLine = usePageLine();
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);

  // The tab is read straight from the URL, so a link can point at either tab and the
  // strip can never disagree with the address.
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get('tab');
  const activeTab: Tab = fromUrl === 'questionnaires' || fromUrl === 'plan' ? fromUrl : 'targets';

  // A callback ref (not useRef): the tabs must re-render once the node exists so they
  // can portal their button into it.
  const [actionSlot, setActionSlot] = useState<HTMLDivElement | null>(null);

  // The project's questionnaires (null until known): counted in the strip, and per role
  // on Visit targets.
  const [questionnaires, setQuestionnaires] = useState<VisitQuestionnaire[] | null>(null);
  // The role whose "n active" was clicked on Visit targets: the Questionnaires tab opens
  // filtered to it. Kept with its project, so it can never filter another project's list.
  const [roleHandoff, setRoleHandoff] = useState<{ projectId: number; roleId: string } | null>(null);

  // Whether Visit targets (or the Pre-Journey Plan) holds unsaved edits. Refs, not state:
  // they are only read at the moment a tab is clicked.
  const targetsDirty = useRef(false);
  const onTargetsDirtyChange = useCallback((dirty: boolean) => {
    targetsDirty.current = dirty;
  }, []);
  const planDirty = useRef(false);
  const onPlanDirtyChange = useCallback((dirty: boolean) => {
    planDirty.current = dirty;
  }, []);

  // Another project's questionnaires must never be counted as this one's.
  useEffect(() => {
    setQuestionnaires(null);
    setRoleHandoff(null);
  }, [selectedProjectId]);

  // On Visit targets the list is read here, afresh each time the tab is opened, so a
  // questionnaire added or deactivated on the other tab is counted. On Questionnaires
  // the embedded page loads the same list itself and reports it up, so it is not read twice.
  useEffect(() => {
    if (!selectedProjectId || activeTab !== 'targets') return undefined;
    let cancelled = false;
    tasksAPI
      .getAll({ projectId: selectedProjectId, task_type: 'store_visit' })
      .then((res) => {
        if (!cancelled) setQuestionnaires(Array.isArray(res.data?.data) ? res.data.data : null);
      })
      .catch(() => {
        if (!cancelled) setQuestionnaires(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, activeTab]);

  const selectTab = (tab: Tab, roleId?: string) => {
    if (tab === activeTab) return;
    if (targetsDirty.current && !window.confirm(DISCARD_TARGETS_PROMPT)) return;
    if (planDirty.current && !window.confirm(DISCARD_PLAN_PROMPT)) return;
    setRoleHandoff(roleId && selectedProjectId ? { projectId: selectedProjectId, roleId } : null);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  // The same split the Questionnaires tab draws its own Active count from.
  const activeCount = questionnaires ? splitTasksByStatus(questionnaires, { search: '', roleId: '' }).active.length : null;

  return (
    <div className="space-y-6">
      {/* min-w-0: lets the line be cut at the page's edge instead of widening it. */}
      <div className="min-w-0">
        <h1 className="text-2xl font-bold text-gray-900">Store Visits Configuration</h1>
        {pageLine && <p className="mt-1 truncate text-sm text-gray-500" title={pageLine}>{pageLine}</p>}
      </div>

      {/* Tabs left, the open tab's action right — the StoreMasterPage strip. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.key === activeTab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => selectTab(t.key)}
                className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                  isActive ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
                {t.key === 'questionnaires' && activeCount !== null ? ` (${activeCount.toLocaleString()})` : ''}
              </button>
            );
          })}
        </div>
        <div ref={setActionSlot} className="flex items-center gap-3" />
      </div>

      {activeTab === 'targets' && (
        <VisitTargetsTab
          actionSlot={actionSlot}
          questionnaires={questionnaires}
          onOpenQuestionnaires={(roleId) => selectTab('questionnaires', roleId)}
          onDirtyChange={onTargetsDirtyChange}
        />
      )}
      {activeTab === 'questionnaires' && (
        <TasksPage
          variant="store_visit"
          embedded
          actionSlot={actionSlot}
          initialRoleFilter={roleHandoff?.projectId === selectedProjectId ? roleHandoff?.roleId : undefined}
          onTasksLoaded={setQuestionnaires}
        />
      )}
      {activeTab === 'plan' && <PreJourneyPlanTab actionSlot={actionSlot} onDirtyChange={onPlanDirtyChange} />}
    </div>
  );
}
